import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import { ProgressSpinner } from "primereact/progressspinner";

import { UserContext } from "components/user/UserContext";
import { createUser, updateRoles, updateUser, validateUser } from "services/UserService";
import { findRoles } from "services/RoleService";
import { handleError } from "utils/handleError";
import { actionUpdate, actionView } from "utils/action";

export const UserEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["user", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;
    const [title, setTitle] = useState(t("user.titleDetail"));

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const userContext = useContext(UserContext);
    const { user, resetUser, setUser, users, setUsers, submitted, setSubmitted, userDialog, setUserDialog } = userContext;

    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [readOnly, setReadOnly] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const userTypes = [
        { label: t("officer"), value: "OFFICER" },
        { label: t("citizen"), value: "CITIZEN", disabled: true },
        { label: t("foreign"), value: "FOREIGN", disabled: true },
    ];

    const states = [
        { label: t("user.-1"), value: -1 },
        { label: t("user.0"), value: 0 },
        { label: t("user.1"), value: 1 },
        { label: t("user.2"), value: 2 },
        { label: t("user.3"), value: 3 },
        { label: t("user.4"), value: 4 },
        { label: t("user.31"), value: 31 },
        { label: t("user.97"), value: 97 },
        { label: t("user.98"), value: 98 },
        { label: t("user.99"), value: 99 },
    ];

    useEffect(() => {
        findRoles(headers, {
            first: 0,
            rows: 1000,
            page: 0,
        })
            .then((res) => {
                setRoles(res.data);
            })
            .catch((error) => handleError(toast, t, error));
    }, []);

    useEffect(() => {
        setSelectedRoles(user.roles || []);
        if (user.action === actionUpdate) {
            setTitle(t("user.titleEditDetail"));
        } else {
            setTitle(t("user.titleDetail"));
        }
    }, [user]);

    useEffect(() => {
        setReadOnly(user.id && user.type === "CITIZEN");
    }, [user.id]);

    const onInputChange = (e, name) => {
        const val = e.target?.value || e.value || "";
        let _user = { ...user };
        _user[`${name}`] = val;

        setUser(_user);
    };

    const onEnabledChange = (e) => {
        let _user = { ...user };
        _user["enabled"] = e.checked;
        setUser(_user);
    };

    const onRolesChange = (value) => {
        setSelectedRoles(value);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setUserDialog(false);
    };

    const saveUserAction = () => {
        setSubmitted(true);
        setShowSpinner(true);

        validateUser(user)
            .then(() => {
                if (!user.id) {
                    // Creating
                    return createUser(headers, { ...user, roles: selectedRoles }).then((res) => {
                        let _users = [...users];
                        _users.push(res.data);
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.created", { ns: "common", object: t("user") }), life: 3000 });

                        setSelectedRoles([]);
                        setUsers(_users);
                        setUserDialog(false);
                        resetUser();
                    });
                } else {
                    // Updating
                    let params = {
                        fullName: user.fullName,
                        email: user.email,
                        enabled: user.enabled,
                        type: user.type,
                        roles: selectedRoles,
                    };

                    if (user.password?.length > 0) {
                        params["password"] = user.password;
                    }

                    // return updateUser(headers, user.id, params).then(() => {
                    return updateRoles(headers, user.id, selectedRoles).then((res) => {
                        let _users = [...users];
                        const index = findIndexById(user.id);
                        _users[index] = res.data;

                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.updated", { ns: "common", object: t("user") }), life: 3000 });

                        setUsers(_users);
                        setUserDialog(false);
                        resetUser();
                        setSelectedRoles([]);
                        // });
                    });
                }
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setShowSpinner(false));
    };

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < users.length; i++) {
            if (users[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    };

    const dialogFooter = (
        <>
            <Button label={t("cancel", { ns: "common" })} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={t("save", { ns: "common" })} icon="pi pi-check" onClick={saveUserAction} />
        </>
    );

    const convertStatus = () => {
        let text = "";
        if (user.level == 0 && user.state == 0) {
            text = "Tài khoản hoạt động ở mức 0";
        } else if (user.level == 1 && user.state == 1 && !user.isUpdate) {
            text = "Tài khoản chờ kích hoạt thiết bị - Trường hợp cần đổi mật khẩu";
        } else if (user.level == 1 && user.state == 1 && user.isUpdate) {
            text = "Tài khoản chờ kích hoạt thiết bị - Trường hợp không cần đổi mật khẩu";
        } else if (user.level == 1 && user.state == 2) {
            text = "Tài khoản hoạt động mức 1";
        } else if (user.level == 1 && user.state == 97) {
            text = "Tài khoản bị khóa tạm thời";
        } else if (user.level == 1 && user.state == 98) {
            text = "Tài khoản bị khóa tạm thời";
        } else if (user.level == 2 && user.state == 1 && !user.isUpdate) {
            text = "Tài khoản chờ kích hoạt thiết bị - Trường hợp cần đổi mật khẩu";
        } else if (user.level == 2 && user.state == 1 && user.isUpdate) {
            text = "Tài khoản chờ kích hoạt thiết bị - Trường hợp không cần đổi mật khẩu";
        } else if (user.level == 2 && user.state == 2) {
            text = "Tài khoản hoạt động mức 2";
        } else if (user.level == 2 && user.state == 97) {
            text = "Tài khoản bị khóa tạm thời";
        } else if (user.level == 2 && user.state == 98) {
            text = "Tài khoản bị khóa vĩnh viễn";
        }
        return text;
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog visible={userDialog} style={{ width: "70%" }} header={t("user.titleDetail")?.toUpperCase()} modal className="p-fluid text-center" footer="" onHide={hideDialog}>
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="grid">
                                {/* tên đăng nhập */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="username" style={{ fontWeight: "500" }}>
                                        {t("username")}
                                    </label>

                                    <InputText id="username" value={user.username} keyfilter={/^[^&=_+,#<>*!?'"`~\s]+$/} maxLength={50} onChange={(e) => onInputChange(e, "username")} required autoFocus readOnly={user.id} className={classNames({ "p-invalid": submitted && !user.username })} />
                                    {submitted && !user.username && <small className="p-invalid">{t("username.required")}.</small>}
                                </div>

                                {/* <div className="field col-12 md:col-3">
                                    <label htmlFor="password">{t("password")}</label>
                                    <Password id="password" toggleMask value={user.password} onChange={(e) => onInputChange(e, "password")} required readOnly={readOnly} className={classNames({ "p-invalid": submitted && !user.password && !user.id })} />
                                    {submitted && !user.password && !user.id && <small className="p-invalid">{t("password.required")}</small>}
                                </div> */}

                                {/* số điện thoại */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="phoneNumber">{t("phoneNumber")}</label>
                                    <InputText id="phoneNumber" value={user.phoneNumber} maxLength={20} readOnly={true} onChange={(e) => onInputChange(e, "phoneNumber")} />
                                </div>

                                {/* mức tài khoản */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="level">{t("user.level")}</label>
                                    <InputText id="level" value={user.level} maxLength={20} readOnly={true} onChange={(e) => onInputChange(e, "level")} />
                                </div>

                                {/* kích hoạt */}
                                <div className="field col-12 md:col-3" style={{ display: "flex", justifyContent: "center", margin: "auto" }}>
                                    <label htmlFor="enabled">{t("enabled")}</label>
                                    <Checkbox id="enabled" name="enabled" checked={user.enabled} readOnly={true} onChange={onEnabledChange} />
                                </div>
                            </div>

                            <div className="grid">
                                {/* họ và tên */}
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="fullName">{t("fullName")}</label>
                                    <InputText id="fullName" value={user.fullName} maxLength={100} onChange={(e) => onInputChange(e, "fullName")} required readOnly={true} className={classNames({ "p-invalid": submitted && !user.fullName })} />
                                    {submitted && !user.fullName && <small className="p-invalid">{t("fullName.required")}.</small>}
                                </div>

                                {/* loại người dùng */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="type">{t("type")}</label>
                                    <Dropdown id="type" value={user.type} options={userTypes} disabled={user.id} onChange={(e) => onInputChange(e, "type")} />
                                </div>

                                {/* trạng thái */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="status">{t("status")}</label>
                                    <InputText tooltip={convertStatus()} tooltipOptions={{ position: "top" }} id="status" value={convertStatus()} readOnly={true} />
                                </div>
                            </div>

                            <div className="grid">
                                {/* email */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="email">{t("email")}</label>
                                    <InputText id="email" value={user.email} maxLength={100} readOnly={true} onChange={(e) => onInputChange(e, "email")} />
                                </div>

                                {/* ngày hết hạn tài khoản */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="expireDate">{t("user.expireDate")}</label>
                                    <InputText id="expireDate" value={user.expireDate} maxLength={100} readOnly={true} onChange={(e) => onInputChange(e, "expireDate")} />
                                </div>

                                {/* ngày hết hạn thẻ CCCD */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="cardExpireDate">{t("user.cardExpireDate")}</label>
                                    <InputText id="cardExpireDate" value={user.cardExpireDate} maxLength={100} readOnly={true} onChange={(e) => onInputChange(e, "cardExpireDate")} />
                                </div>

                                {/* roles */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="roles">{t("roles")}</label>
                                    <MultiSelect id="roles" name="roles" value={selectedRoles} onChange={(e) => onRolesChange(e.value)} options={roles} optionLabel="name" disabled={user.action !== actionUpdate} />
                                </div>
                            </div>

                            <div className="grid">
                                {/* người tạo */}
                                <div className="field col-12 md:col-3">
                                    <label style={{ fontWeight: "500" }} htmlFor="createdBy">
                                        {t("createdBy", { ns: "common" })}
                                    </label>
                                    <InputText id="createdBy" name="createdBy" value={user?.createdBy?.username} readOnly />
                                </div>

                                {/* ngày tạo */}
                                <div className="field col-12 md:col-3">
                                    <label style={{ fontWeight: "500" }} htmlFor="createdDate">
                                        {t("createdDate", { ns: "common" })}
                                    </label>
                                    <InputText id="createdDate" value={user?.createdDate} readOnly />
                                </div>

                                {/* người sửa */}
                                <div className="field col-12 md:col-3">
                                    <label style={{ fontWeight: "500" }} htmlFor="modifiedBy">
                                        {t("modifiedBy", { ns: "common" })}
                                    </label>
                                    <InputText id="modifiedBy" value={user?.modifiedBy?.username} readOnly />
                                </div>

                                {/* ngày sửa */}
                                <div className="field col-12 md:col-3">
                                    <label style={{ fontWeight: "500" }} htmlFor="modifiedDate">
                                        {t("modifiedDate", { ns: "common" })}
                                    </label>
                                    <InputText id="modifiedDate" value={user?.modifiedDate} readOnly />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid">
                    <div className="col-12">
                        {/* chỉnh sửa */}
                        {user.action !== actionView && (
                            <Button
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2"
                                label={t("save", { ns: "common" })}
                                icon="pi pi-check"
                                onClick={saveUserAction}
                            />
                        )}
                        {/* đóng */}
                        <Button
                            style={{
                                width: "max-content",
                            }}
                            className="mr-2 p-button-danger"
                            label={t("close", { ns: "common" })}
                            icon="pi pi-times"
                            onClick={hideDialog}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog visible={showSpinner} closable={false} resizable={false} draggable={false} showHeader={false}>
                <ProgressSpinner />
            </Dialog>
        </>
    );
};
